/*
 * Copyright (C) 2026 Nex4Nova (Geoblast group)
 * SPDX-License-Identifier: Proprietary
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import './Sidebar.css';
import { useZoomContext } from '../../contexts/ZoomContext.jsx';
import { useProjectData } from '../../contexts/ProjectDataContext.jsx';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useLanguage } from '../../contexts/LanguageContext.jsx';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher.jsx';
import { isTauri } from '../../lib/projectIo.js';

const MENU = [
  { path: '/', key: 'nav.map' },
  { path: '/strip-log', key: 'nav.stripLogs' },
  { path: '/drillhole', key: 'nav.scene3d' },
  { path: '/analytics', key: 'nav.analytics' },
];

const AUTO_LOAD_KEY = 'nex4nova-drilling-autoload';
const LAST_DEMO_KEY = 'nex4nova-drilling-last-demo';
const DEMO_PATHS = {
  'demo-nex4nova': 'test-data/demo-nex4nova',
  'demo-yilgarn': 'test-data/demo-yilgarn',
  'demo-forrestania': 'test-data/demo-forrestania',
};

function Sidebar() {
  const location = useLocation();
  const { zoomLevel } = useZoomContext();
  const { theme, setTheme } = useTheme();
  const { t } = useLanguage();
  const project = useProjectData();
  const folderInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const [autoLoad, setAutoLoad] = useState(() => {
    try {
      const value = localStorage.getItem(AUTO_LOAD_KEY);
      return value === null ? true : value === 'true';
    } catch { return true; }
  });
  const [demoLoading, setDemoLoading] = useState('');
  const [zipError, setZipError] = useState('');

  const onOpenClick = async () => {
    if (isTauri()) {
      try {
        await project.openProject();
      } catch (e) {
        console.error('openProject failed', e);
      }
    } else {
      folderInputRef.current?.click();
    }
  };

  const onFolderInputChange = async (event) => {
    const files = event.target.files;
    if (!files || !files.length) return;
    try {
      await project.openProjectFromFileList(files);
    } catch (e) {
      console.error('openProjectFromFileList failed', e);
    }
    event.target.value = '';
  };

  const onZipInputChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setZipError('');
    try {
      const { zipEntriesToFileList } = await import('../../lib/zipImport.js');
      const { files, pathParts } = await zipEntriesToFileList(file);
      if (!files.length) {
        setZipError(t('project.zipInvalid'));
        return;
      }
      const folderPath = pathParts.length === 1 ? pathParts[0] : file.name.replace(/\.zip$/i, '');
      await project.openProjectFromFileList(files, { folderPath });
    } catch (e) {
      console.error('zip import failed', e);
      setZipError(t('project.zipInvalid'));
    }
    event.target.value = '';
  };

  const loadDemo = async (key, path) => {
    setDemoLoading(key);
    try {
      if (isTauri()) {
        const { readProjectFolder } = await import('../../lib/projectIo.js');
        const read = await readProjectFolder(path);
        await project.openProjectFromFileList(convertReadToFileList(read), { rename: false });
      } else {
        const base = window.location.origin;
        const files = [];
        const names = ['collars', 'survey', 'assays', 'geology', 'structure', 'surface_samples'];
        for (const name of names) {
          const resp = await fetch(`${base}/${path}/${name}.csv`);
          if (resp.ok) {
            const text = await resp.text();
            files.push(new File([text], `${name}.csv`, { type: 'text/csv' }));
          }
        }
        if (files.length) {
          await project.openProjectFromFileList(files, { rename: false });
          try { localStorage.setItem(LAST_DEMO_KEY, key); } catch (e) { /* ignore */ }
        } else {
          console.warn('No demo files found at', path);
        }
      }
    } catch (e) {
      console.error('Failed to load demo:', e);
    } finally {
      setDemoLoading('');
    }
  };

  const downloadDemo = async (key, path) => {
    try {
      const { downloadDemoProject } = await import('../../lib/demoDownload.js');
      await downloadDemoProject(window.location.origin, path, `${key}.zip`);
    } catch (e) {
      console.error('Failed to download demo:', e);
    }
  };

  useEffect(() => {
    try { localStorage.setItem(AUTO_LOAD_KEY, String(autoLoad)); } catch { }
  }, [autoLoad]);

  const folderLabel = useMemo(() => {
    if (project.projectLabel) return project.projectLabel;
    if (project.folderPath) return shortenPath(project.folderPath);
    if (project.collars.length > 0) {
      const names = [...new Set(project.collars.map((c) => c.project).filter(Boolean))];
      if (names.length) return names.join(', ');
      try {
        const demo = localStorage.getItem(LAST_DEMO_KEY);
        if (demo) return demo.replace('demo-', '');
      } catch (e) { /* ignore */ }
    }
    return t('project.noProject');
  }, [project.folderPath, project.projectLabel, project.collars, t]);

  return (
    <nav className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo-row">
          <Nex4NovaLogo />
        </div>
        <h1 className="brand-title">{t('app.title')}</h1>
        <span className="sidebar-version">v{__APP_VERSION__}</span>
      </div>

      <LanguageSwitcher />

      <ul className="sidebar-menu">
        {MENU.map((item) => (
          <li key={item.path}>
            <Link to={item.path} className={location.pathname === item.path ? 'active' : ''}>
              <span className="label">{t(item.key)}</span>
            </Link>
          </li>
        ))}
      </ul>

      <div className="sidebar-section">
        <div className="label-caps">{t('project.section')}</div>
        <Link
          to="/data-instructions"
          className={`sidebar-section-link${location.pathname === '/data-instructions' ? ' active' : ''}`}
        >
          {t('project.dataFormat')}
        </Link>
        <button type="button" className="primary-button sidebar-open-btn" onClick={onOpenClick}>
          {project.status === 'loading' ? t('project.loading') : t('project.openFolder')}
        </button>
        <input
          ref={folderInputRef}
          type="file"
          webkitdirectory=""
          directory=""
          multiple
          hidden
          onChange={onFolderInputChange}
        />
        <button type="button" className="ghost-button small sidebar-zip-btn" onClick={() => zipInputRef.current?.click()}>
          {t('project.uploadZip')}
        </button>
        <input
          ref={zipInputRef}
          type="file"
          accept=".zip,application/zip"
          hidden
          onChange={onZipInputChange}
        />
        <div className="sidebar-folder-path" title={project.folderPath}>{folderLabel}</div>
        {zipError && <div className="error-banner small">{zipError}</div>}
        {project.status === 'error' && project.errors?.load && (
          <div className="error-banner small">{project.errors.load}</div>
        )}
        {project.status === 'ready' && (
          <button type="button" className="ghost-button small sidebar-close-btn" onClick={project.closeProject}>
            {t('project.close')}
          </button>
        )}
      </div>

      {project.status !== 'ready' && (
        <div className="sidebar-section sidebar-demo-section">
          <div className="label-caps">{t('demo.section')}</div>
          {Object.entries(DEMO_PATHS).map(([key, path]) => (
            <div key={key} className="sidebar-demo-row">
              <button
                type="button"
                className="sidebar-demo-btn"
                onClick={() => loadDemo(key, path)}
                disabled={demoLoading === key || project.status === 'loading'}
              >
                {demoLoading === key ? t('project.loading') : (
                  <>
                    <span className="demo-btn-icon">▶</span>
                    <span>{key.replace('demo-', '')}</span>
                  </>
                )}
              </button>
              <button
                type="button"
                className="sidebar-demo-download"
                title={t('demo.download')}
                aria-label={`${t('demo.download')} ${key.replace('demo-', '')}`}
                onClick={() => downloadDemo(key, path)}
              >
                ⬇
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="sidebar-section sidebar-options-section">
        <label className="sidebar-checkbox-label">
          <input
            type="checkbox"
            checked={autoLoad}
            onChange={(e) => setAutoLoad(e.target.checked)}
          />
          <span>{t('options.autoLoad')}</span>
        </label>
      </div>

      <div id="map-controls-slot" className="sidebar-slot" />
      <div id="strip-log-controls-slot" className="sidebar-slot" />
      <div id="three-d-controls-slot" className="sidebar-slot" />
      <div id="analytics-controls-slot" className="sidebar-slot" />

      <div className="sidebar-footer">
        <div id="data-source-slot" className="data-source-info" />
        {location.pathname === '/' && project.status === 'ready' && (
          <span className="zoom-label">{t('map.zoom', { level: Number(zoomLevel).toFixed(2) })}</span>
        )}
      </div>

      <div className="sidebar-footer-actions">
        <div className="theme-toggle" role="group" aria-label={t('theme.theme')}>
          <button
            type="button"
            className={`theme-toggle-btn${theme === 'light' ? ' active' : ''}`}
            onClick={() => setTheme('light')}
            aria-pressed={theme === 'light'}
            aria-label={t('theme.light')}
            title={t('theme.light')}
          >
            <SunIcon />
          </button>
          <button
            type="button"
            className={`theme-toggle-btn${theme === 'dark' ? ' active' : ''}`}
            onClick={() => setTheme('dark')}
            aria-pressed={theme === 'dark'}
            aria-label={t('theme.dark')}
            title={t('theme.dark')}
          >
            <MoonIcon />
          </button>
        </div>
        <a href="https://nex4nova.com" target="_blank" rel="noopener noreferrer" className="source-link">
          Nex4Nova
        </a>
      </div>
    </nav>
  );
}

function Nex4NovaLogo() {
  return (
    <svg viewBox="0 0 180 32" width="180" height="32" fill="none" aria-label="Nex4Nova">
      <defs>
        <linearGradient id="logoN4N" x1="28" y1="4" x2="10" y2="32" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FC00A5" />
          <stop offset="1" stopColor="#6401E1" />
        </linearGradient>
      </defs>
      <g transform="translate(3, 1) scale(0.1)">
        <path
          d="M287.192 110.824L268.716 134.647L244.908 116.171L263.369 92.3629L191.93 36.9367L173.469 60.7591L149.646 42.2837L168.122 18.4754L144.313 0L0 186.034L166.687 315.348L311 129.3L287.192 110.824Z"
          fill="url(#logoN4N)"
        />
      </g>
      <text
        x="38"
        y="23"
        fill="url(#logoN4N)"
        fontFamily="Lexend, Inter, system-ui, sans-serif"
        fontSize="18"
        fontWeight="700"
        letterSpacing="3.5"
      >NEX4NOVA</text>
    </svg>
  );
}

function convertReadToFileList(read) {
  const files = [];
  for (const [baseName, content] of Object.entries(read.files)) {
    const ext = read.formats[baseName] || 'csv';
    files.push(new File([content], `${baseName}.${ext}`, { type: 'text/csv' }));
  }
  return files;
}

function shortenPath(p) {
  if (!p) return '';
  if (p.length <= 36) return p;
  const head = p.slice(0, 12);
  const tail = p.slice(-20);
  return `${head}…${tail}`;
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="M4.93 4.93l1.41 1.41" />
      <path d="M17.66 17.66l1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="M4.93 19.07l1.41-1.41" />
      <path d="M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

export default Sidebar;
